import { ModalService } from '@app/shared/services/modal.service';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Inject,
} from '@angular/core';
import { WindowToken } from '@app/shared/window.token';
import { switchMap, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { SessionTimeoutModalComponent } from '@app/session/session-timeout-modal/session-timeout-modal.component';
import { SessionService } from '@app/session/session.service';

@Component({
  selector: 'dgx-session-timeout',
  template: '<!--no template necessary-->',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionTimeoutComponent implements OnInit {
  private modalTimer: number;

  constructor(
    private sessionService: SessionService,
    private modalService: ModalService,
    @Inject(WindowToken) private windowRef: Window
  ) {}

  public ngOnInit(): void {
    this.startModalTimer();
  }

  public ngOnDestroy(): void {
    this.windowRef.clearTimeout(this.modalTimer);
  }

  /**
   * Start/reset the timer for the session timeout modal
   */
  private startModalTimer(): void {
    // Skip if no session timeout value is set for the org
    if (!this.sessionService.expiration) {
      return;
    }

    // Delay before showing modal
    const showModalTimeout = this.sessionService.showModalTimeout;

    // If it's too early to show the modal, sleep until it's time and restart
    // this function which will update the time to expiration in case it changed.
    if (showModalTimeout > 0) {
      this.modalTimer = this.getModalTimer(showModalTimeout);
      // Stop here, we don't need to open the modal or set the session timer yet
      return;
    }

    // Modal timeout has expired, show the modal
    this.showSessionTimeoutModal().subscribe();
  }

  /**
   * Show session timeout modal and wait for user feedback
   */
  private showSessionTimeoutModal(): Observable<void> {
    // Start a timer that will resolve after the session has expired.
    const sessionTimer = this.getSessionTimer(
      this.sessionService.sessionTimeout
    );

    return this.modalService.show(SessionTimeoutModalComponent, undefined).pipe(
      tap(() =>
        // The user chose to extend the session, stop session timer
        this.windowRef.clearTimeout(sessionTimer)
      ),
      switchMap(() =>
        // Renew the session
        this.sessionService.renewSessionTimeout()
      ),
      tap(() =>
        // Restart the modal timer
        this.startModalTimer()
      )
    );
  }

  /**
   * Get timer for modal display
   *
   * @param timeout seconds
   */
  private getModalTimer(timeout: number): number {
    const delayInMS = timeout * 1000;
    return this.windowRef.setTimeout(() => {
      this.startModalTimer();
    }, delayInMS);
  }

  /**
   * Get get timer for ending session
   *
   * @param timeout seconds
   */
  private getSessionTimer(timeout: number): number {
    const delayInMS = (timeout + 1) * 1000; // +1 second so timer reaches 0
    return this.windowRef.setTimeout(() => {
      this.windowRef.location.reload();
    }, delayInMS);
  }
}
